import Service, { type Registry as Services, service } from '@ember/service';
import type { AxiosResponse } from 'axios';
import type { UploadFile } from 'ember-file-upload/upload-file';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import type { RhsRecord } from 'uplisting-frontend/utils/rhs/types';
import { getRhsRecordKey } from 'uplisting-frontend/utils/rhs/helpers';

export interface UploadResult {
  s3Url: string;
}

export interface ChecksummedUploadResult {
  bucket: string;
  key: string;
  digest: string;
}

export interface AirbnbMediaUploadResult {
  signedUrl: string;
  mimeType: string;
}

export default class FileHandlerService extends Service {
  @service store!: Services['store'];
  @service httpClient!: Services['http-client'];

  public async uploadS3File(
    file: UploadFile,
  ): Promise<ModelRegistry['upload']> {
    const { name, type } = file.file;
    const upload = this.store.createRecord('upload', {
      filename: name,
      contentType: type,
    });

    await upload.save();
    return upload;
  }

  public async uploadFile(file: UploadFile): Promise<UploadResult> {
    const upload = await this.uploadS3File(file);
    await file.upload(upload.url, {
      data: {
        ...upload.fields,
      },
    });

    return { s3Url: upload.cdnUrl };
  }

  // Evidence-grade attachments (task photos) need a digest S3 verifies against the uploaded
  // bytes, not one the client merely asserts — see TaskAttachment::Confirm on the API. S3 only
  // checks the checksum if it's told to expect one at presign time (`checksum_algorithm`), and
  // the value itself has to come from here: the server never sees the file's bytes.
  public async uploadFileWithChecksum(
    file: UploadFile,
  ): Promise<ChecksummedUploadResult> {
    const { name, type } = file.file;
    const digest = await this.sha256Base64(file);

    const upload = this.store.createRecord('upload', {
      filename: name,
      contentType: type,
      checksumAlgorithm: 'SHA256',
    });

    await upload.save();

    await file.upload(upload.url, {
      data: {
        ...upload.fields,
        'x-amz-checksum-algorithm': 'SHA256',
        'x-amz-checksum-sha256': digest,
      },
    });

    return { bucket: upload.bucket, key: upload.key, digest };
  }

  private async sha256Base64(file: UploadFile): Promise<string> {
    const bytes = await file.file.arrayBuffer();
    const digest = await window.crypto.subtle.digest('SHA-256', bytes);

    return window.btoa(String.fromCharCode(...new Uint8Array(digest)));
  }

  public async uploadAirbnbMedia(
    file: UploadFile,
    record: RhsRecord,
  ): Promise<AirbnbMediaUploadResult> {
    const mimeType = file.file.type;
    const instance = await this.httpClient.getInstance();

    const relationshipKey = getRhsRecordKey(record);

    const response = await instance.post(
      '/v2/airbnb_official_media_upload_urls',
      {
        data: {
          attributes: { mime_type: mimeType },
          relationships: {
            [relationshipKey]: { data: { id: Number(record.id) } },
          },
        },
      },
    );

    const signedUrl = response.data.signed_url;

    const { default: axios } = await import('axios');

    await axios.put(signedUrl, file.file, {
      headers: { 'Content-Type': mimeType },
    });

    return { signedUrl, mimeType };
  }

  public async downloadFile(url: string, data: object): Promise<void> {
    const instance = await this.httpClient.getInstance();

    try {
      const response = await instance.post(
        url,
        { data },
        {
          responseType: 'arraybuffer',
        },
      );

      this.#saveResponse(response, 'download.zip');
    } catch (error) {
      throw this.#decodeErrorBody(error);
    }
  }

  public async getFile(url: string, params: object): Promise<void> {
    const instance = await this.httpClient.getInstance();

    try {
      const response = await instance.get(url, {
        params,
        responseType: 'arraybuffer',
      });

      this.#saveResponse(response, 'download');
    } catch (error) {
      throw this.#decodeErrorBody(error);
    }
  }

  #decodeErrorBody(error: unknown): unknown {
    if (!this.httpClient.isAxiosError?.(error) || !error.response) {
      return error;
    }

    const { data } = error.response;
    const text =
      data instanceof ArrayBuffer ? new TextDecoder().decode(data) : data;

    if (typeof text !== 'string' || text === '') {
      return error;
    }

    try {
      error.response.data = JSON.parse(text);
    } catch {
      return error;
    }

    return error;
  }

  #saveResponse(
    response: AxiosResponse<ArrayBuffer>,
    fallbackFilename: string,
  ): void {
    // Extract filename from Content-Disposition header
    const disposition = response.headers['content-disposition'] as string;
    const filenameMatch = disposition
      ?.split(';')
      ?.find((part) => part.trim().startsWith('filename='))
      ?.split('=')[1]
      ?.replace(/"/g, '')
      ?.trim();

    const filename = filenameMatch || fallbackFilename;

    // eslint-disable-next-line no-restricted-globals
    const blob = new Blob([response.data], {
      type: response.headers['content-type'] as string | undefined,
    });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = window.document.createElement('a');

    link.href = downloadUrl;
    link.download = filename;
    link.style.visibility = 'hidden';
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }
}

declare module '@ember/service' {
  interface Registry {
    'file-handler': FileHandlerService;
  }
}
